/** Installer: Social Posting — connect Facebook/Instagram, post now or schedule, use Ox Resin's ready-made posts. */
import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader2, Share2 } from 'lucide-react';
import { toast } from 'sonner';
import ProPortalHeader from '@/components/ProPortalHeader';
import { wget } from '@/components/website/api';
import { ConnectedAccounts } from '@/components/social/ConnectedAccounts';
import { SocialComposer } from '@/components/social/SocialComposer';
import { PostHistory } from '@/components/social/PostHistory';
import { SuggestedPosts } from '@/components/social/SuggestedPosts';
import { InsightsSummary } from '@/components/social/PostMetrics';

export default function SocialHub() {
  const navigate = useNavigate();
  const location = useLocation();
  const [status, setStatus] = useState(null);
  const [posts, setPosts] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [prefill, setPrefill] = useState(location.state?.prefill || null);

  const load = useCallback(async () => {
    try {
      const [s, p, sg] = await Promise.all([wget('/social/status'), wget('/social/posts'), wget('/social/suggestions')]);
      setStatus(s); setPosts(p); setSuggestions(sg);
    } catch (e) { toast.error(e.message); }
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const q = new URLSearchParams(location.search).get('connect');
    if (q === 'ok') toast.success('Facebook connected — your pages are listed below');
    if (q === 'cancelled') toast.error('Facebook login was cancelled');
    if (q === 'failed') toast.error('Facebook connection failed — please try again');
    if (q) navigate('/marketing/social', { replace: true });
  }, [location.search, navigate]);
  useEffect(() => {
    if (!posts.some((p) => p.status === 'publishing')) return undefined;
    const t = setInterval(() => wget('/social/posts').then(setPosts).catch(() => {}), 5000);
    return () => clearInterval(t);
  }, [posts]);

  if (!status) return <div className="min-h-screen bg-[#E8ECEF]"><ProPortalHeader /><div className="p-10 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-slate-400" /></div></div>;
  return (
    <div className="min-h-screen bg-[#E8ECEF]" data-testid="social-hub">
      <ProPortalHeader />
      <main className="max-w-5xl mx-auto px-4 py-6 md:py-10 space-y-5">
        <div className="flex items-center gap-3 text-sm text-slate-600">
          <button onClick={() => navigate('/')} className="inline-flex items-center gap-1 hover:text-slate-900" data-testid="social-back-home"><ArrowLeft className="w-4 h-4" /> Home</button>
          <span>/</span>
          <button onClick={() => navigate('/marketing')} className="hover:text-slate-900" data-testid="social-back-marketing">Marketing Hub</button>
        </div>
        <div>
          <h1 className="text-3xl font-bold text-[#27292E] flex items-center gap-2"><Share2 className="w-7 h-7 text-blue-700" /> Social Posting</h1>
          <p className="text-slate-600 mt-1">Post to your Facebook page and Instagram straight from here — now, or scheduled for later.</p>
        </div>
        <ConnectedAccounts status={status} onChanged={load} />
        <SuggestedPosts items={suggestions} onUse={(s) => setPrefill({ caption: s.caption, media: s.media, suggestion_id: s.id })} />
        <SocialComposer accounts={status.accounts} prefill={prefill} onConsumePrefill={() => setPrefill(null)} onPosted={() => load()} />
        <InsightsSummary posts={posts} />
        <PostHistory posts={posts} onChanged={load} />
      </main>
    </div>
  );
}
